import React from "react";
import { TrendingDown, Check } from "lucide-react";

const TIERS = [
    {
        qty: 3,
        label: "3 plates",
        discount: 0,
        sub: "Minimum order",
        tone: "border-mint bg-white",
        badge: "bg-mint text-forest",
        priceColor: "text-forest",
    },
    {
        qty: 6,
        label: "6 plates",
        discount: 10,
        sub: "Save 10%",
        tone: "border-sage bg-white",
        badge: "bg-sage text-white",
        priceColor: "text-forest",
    },
    {
        qty: 9,
        label: "9 plates",
        discount: 15,
        sub: "Save 15%",
        tone: "border-gold/60 bg-white",
        badge: "bg-gold text-navy",
        priceColor: "text-burnt",
        featured: true,
    },
    {
        qty: 12,
        label: "12+ plates",
        discount: 20,
        sub: "Save 20% — Best value",
        tone: "border-burnt/60 bg-white",
        badge: "bg-burnt text-white",
        priceColor: "text-burnt",
    },
];

export default function VolumePricing({ onCta }) {
    return (
        <section
            data-testid="volume-pricing-section"
            className="bg-cream py-16 md:py-24 px-6 sm:px-8"
        >
            <div className="max-w-6xl mx-auto">
                <div className="text-center max-w-2xl mx-auto reveal">
                    <span
                        data-testid="volume-pill"
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-burnt/10 text-burnt text-xs font-bold tracking-widest uppercase"
                    >
                        <TrendingDown size={14} /> Buy more, save more
                    </span>
                    <h2 className="mt-5 font-serif font-black text-3xl sm:text-4xl md:text-5xl leading-tight text-navy">
                        The more you order,{" "}
                        <span className="text-forest italic">
                            the less you pay.
                        </span>
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-muted2 leading-relaxed">
                        Mix &amp; match any combination of single meals.
                        Discounts kick in automatically at checkout. Minimum
                        order is{" "}
                        <span className="font-bold text-navy">3 plates</span>.
                    </p>
                </div>

                <ul
                    data-testid="volume-tiers-grid"
                    className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
                >
                    {TIERS.map((t) => (
                        <li
                            key={t.qty}
                            data-testid={`volume-tier-${t.qty}`}
                            className={`reveal relative rounded-[20px] border-2 p-5 sm:p-6 shadow-[0_8px_24px_rgba(13,40,24,0.06)] transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_14px_32px_rgba(13,40,24,0.12)] ${t.tone} ${t.featured ? "lg:scale-[1.04]" : ""}`}
                        >
                            {t.featured && (
                                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gold text-navy text-[10px] font-bold uppercase tracking-widest shadow-md">
                                    Most popular
                                </span>
                            )}
                            <span
                                className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${t.badge}`}
                            >
                                {t.label}
                            </span>
                            <p
                                className={`mt-3 font-display text-5xl sm:text-6xl leading-none tabular-nums ${t.priceColor}`}
                            >
                                {t.discount}%
                            </p>
                            <p className="mt-1 text-xs uppercase tracking-widest font-bold text-muted2">
                                {t.discount === 0
                                    ? "Full price"
                                    : `off your order`}
                            </p>
                            <p className="mt-4 text-sm text-navy/85 leading-snug">
                                {t.sub}
                            </p>
                        </li>
                    ))}
                </ul>

                <div className="mt-10 grid sm:grid-cols-3 gap-3 text-sm max-w-3xl mx-auto text-muted2">
                    <div className="flex items-start gap-2">
                        <Check size={16} className="text-forest mt-0.5 flex-none" />
                        <span>Mix any meals — discount auto-applies</span>
                    </div>
                    <div className="flex items-start gap-2">
                        <Check size={16} className="text-forest mt-0.5 flex-none" />
                        <span>3-plate minimum to check out</span>
                    </div>
                    <div className="flex items-start gap-2">
                        <Check size={16} className="text-forest mt-0.5 flex-none" />
                        <span>Family plates priced separately</span>
                    </div>
                </div>

                {onCta && (
                    <div className="mt-10 flex justify-center">
                        <button
                            data-testid="volume-pricing-cta"
                            onClick={onCta}
                            className="rounded-full bg-gold hover:bg-goldDark text-navy font-bold px-7 py-3.5 transition-all active:scale-95 shadow-[0_10px_28px_rgba(244,164,53,0.35)]"
                        >
                            Build your box →
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}
